import { useState } from "react";
import ProductImage from "@/components/ProductImage";
import { Plus, Minus, ShoppingCart, Package, Tag, Sparkles } from "lucide-react";
import { productImageUrl, formatMoney, effectivePrice, priceOptions, unitMrpFor, offerText, offerTypeLabel, offerValidityText, activeBrandOffer, brandOfferValidityText, applicableOffer, offerBuyText } from "@/lib/api";

// Very light pastel frames (cream, blue, green, peach, lavender), stable per product.
const PASTELS = ["bg-[#FBF6EA]", "bg-[#EEF4FB]", "bg-[#EEF7F0]", "bg-[#FCF1EA]", "bg-[#F3F0FA]"];
const pastelFor = (id = "") => PASTELS[[...String(id)].reduce((n, c) => n + c.charCodeAt(0), 0) % PASTELS.length];

export default function ProductCard({ product, onAdd }) {
  const [qty, setQty] = useState(1);
  const options = priceOptions(product);
  const [optIdx, setOptIdx] = useState(0);
  const hasOffer = !!product.offer_active;
  const offerLine = offerText(product);
  const offerValidity = offerValidityText(product);

  const selected = options[optIdx] || options[0] || null;
  const effective = selected ? selected.rate : effectivePrice(product);

  // Unit-aware MRP display (Piece MRP for Piece, Case MRP for Case, etc.)
  const sel = selected;
  const unitMrp = sel ? unitMrpFor(product, sel.unit) : null;
  const showMrpLine = unitMrp != null;

  // Unified Buy X → discount + free item offer (brand-scope shown as BRAND OFFER)
  const uOffer = applicableOffer(product);
  const brandOffer = uOffer && uOffer.scope === "brand" ? activeBrandOffer(product) : null;
  const brandOfferLine = uOffer && uOffer.scope === "brand" ? offerBuyText(uOffer) : null;
  const brandOfferValidity = brandOfferValidityText(brandOffer);

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-blue-100 bg-white shadow-sm transition hover:shadow-md hover:border-blue-200"
    >
      <div className="relative w-full border-b border-slate-100 bg-white">
        <ProductImage bg={pastelFor(product.id)} blend path={product.image_path} alt={product.name} size="md" iconClass="h-12 w-12" className="w-full" testId={`product-image-${product.id}`} />
        <div className="absolute left-2 top-2 flex flex-col items-start gap-1">
          {hasOffer && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-bold text-white shadow" data-testid={`offer-badge-${product.id}`}>
              <Tag className="h-3 w-3" />
              {offerTypeLabel(product.offer_type) || "Offer"}
            </span>
          )}
          {brandOffer && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-bold text-white shadow" data-testid={`brand-offer-badge-${product.id}`}>
              <Sparkles className="h-3 w-3" />
              BRAND OFFER
            </span>
          )}
        </div>
        {!product.in_stock && (
          <span className="absolute right-2 top-2 rounded-full bg-slate-700 px-2 py-0.5 text-[11px] font-semibold text-white">
            Out of stock
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-500">{product.brand}</p>
        <h3 className="mt-0.5 line-clamp-2 min-h-[2.5rem] text-sm font-semibold text-slate-800">
          {product.name}
          {product.pack_size ? <span className="font-normal text-slate-400"> · {product.pack_size}</span> : null}
        </h3>

        <div className="mt-2 space-y-0.5">
          <div className="h-4">
            {showMrpLine && (
              <p className="text-xs text-slate-400" data-testid={`mrp-${product.id}`}>
                MRP: <span className="line-through">{formatMoney(unitMrp)}</span>
              </p>
            )}
          </div>
          {formatMoney(effective) ? (
            <p className="flex flex-wrap items-baseline gap-x-1.5">
              <span className="text-[11px] font-medium text-slate-500">Selling Price:</span>
              <span className="text-base font-bold text-blue-700">{formatMoney(effective)}</span>
              {selected && selected.label && (
                <span className="text-xs font-medium text-slate-500">/ {selected.label}</span>
              )}
            </p>
          ) : (
            <span className="text-sm font-medium text-slate-400">Price on request</span>
          )}
        </div>

        {/* All entered unit rates (only non-empty; empty units are hidden) */}
        {(options.length > 0 || product.case_quantity) && (
          <div className="mt-2 rounded-lg bg-blue-50/60 p-2" data-testid={`rates-${product.id}`}>
            {options.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {options.map((o, i) => (
                  <span
                    key={i}
                    data-testid={`rate-${product.id}-${o.label}`}
                    className="inline-flex items-center gap-1 rounded-md border border-blue-100 bg-white px-2 py-0.5 text-[11px] font-semibold"
                  >
                    <span className="text-slate-500">{o.label}</span>
                    <span className="text-blue-700">{formatMoney(o.rate)}</span>
                  </span>
                ))}
              </div>
            )}
            {product.case_quantity ? (
              <p className={`text-[11px] font-medium text-slate-500 ${options.length > 0 ? "mt-1.5" : ""}`}>
                1 Case = {product.case_quantity} pcs
              </p>
            ) : null}
          </div>
        )}

        {hasOffer && (offerLine || offerTypeLabel(product.offer_type)) && (
          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5" data-testid={`offer-info-${product.id}`}>
            <p className="flex items-start gap-1.5 text-[11px] font-semibold leading-snug text-amber-700">
              <span className="shrink-0">🎁</span>
              <span>{offerLine || offerTypeLabel(product.offer_type)}</span>
            </p>
            {offerValidity && (
              <p className="mt-0.5 pl-5 text-[10px] font-medium text-amber-600/80" data-testid={`offer-validity-${product.id}`}>
                {offerValidity}
              </p>
            )}
          </div>
        )}

        {brandOffer && brandOfferLine && (
          <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1.5" data-testid={`brand-offer-info-${product.id}`}>
            <p className="flex items-start gap-1.5 text-[11px] font-semibold leading-snug text-indigo-700">
              <span className="shrink-0">🎁</span>
              <span>BRAND OFFER: {brandOfferLine}</span>
            </p>
            {brandOfferValidity && (
              <p className="mt-0.5 pl-5 text-[10px] font-medium text-indigo-500" data-testid={`brand-offer-validity-${product.id}`}>
                {brandOfferValidity}
              </p>
            )}
          </div>
        )}

        <div className="mt-auto pt-3">
          {product.in_stock && options.length > 0 ? (
            <div className="space-y-2">
              {options.length > 1 && (
                <select
                  data-testid={`unit-select-${product.id}`}
                  value={optIdx}
                  onChange={(e) => { setOptIdx(Number(e.target.value)); setQty(1); }}
                  className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-700 outline-none focus:border-blue-500"
                >
                  {options.map((o, i) => (
                    <option key={i} value={i}>
                      {o.label} — {formatMoney(o.rate)}
                    </option>
                  ))}
                </select>
              )}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex items-center justify-between rounded-lg border border-slate-200 sm:justify-start">
                  <button data-testid={`qty-dec-${product.id}`} onClick={() => setQty((q) => Math.max(1, (Number(q) || 1) - 1))} className="grid h-9 w-9 place-items-center text-slate-500 hover:text-blue-600 sm:h-8 sm:w-8" aria-label="Decrease quantity">
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    data-testid={`qty-value-${product.id}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    aria-label="Quantity"
                    value={qty === 0 ? "" : qty}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/[^0-9]/g, "");
                      setQty(digits === "" ? 0 : parseInt(digits, 10));
                    }}
                    onBlur={() => setQty((q) => (Number(q) >= 1 ? Number(q) : 1))}
                    className="h-9 w-14 border-x border-slate-200 bg-white text-center text-sm font-semibold text-slate-700 outline-none focus:bg-blue-50 sm:h-8 sm:w-12"
                  />
                  <button data-testid={`qty-inc-${product.id}`} onClick={() => setQty((q) => (Number(q) || 0) + 1)} className="grid h-9 w-9 place-items-center text-slate-500 hover:text-blue-600 sm:h-8 sm:w-8" aria-label="Increase quantity">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <button
                  data-testid={`add-to-cart-${product.id}`}
                  onClick={() => { const q = Number(qty) >= 1 ? Number(qty) : 1; onAdd(product, q, selected); setQty(1); }}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 sm:w-auto sm:flex-1 sm:py-2 sm:text-xs"
                >
                  <ShoppingCart className="h-4 w-4" />
                  Add to Cart
                </button>
              </div>
            </div>
          ) : (
            <button disabled className="w-full cursor-not-allowed rounded-lg bg-slate-100 py-2 text-xs font-semibold text-slate-400">
              {product.in_stock ? "Price on request" : "Unavailable"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
